import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BaseCard from './BaseCard';
import { statBoxStyle, statValueStyle, statLabelStyle } from './cardStyles';
import { ZIMA_BLUE, DARK_GREY } from '../../../dashboardColors';

// Format number with space as thousands separator (works for all locales)
const formatNumberWithSeparator = (num) => {
  if (num === null || num === undefined || isNaN(num)) return 'N/A';
  const rounded = Math.round(num);
  return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const formatWaterSaved = (liters, measurementUnit, unitIsUsg) => {
  if (!liters || liters === 0) return { value: 'N/A', unit: '' };

  // Convert liters to cubic meters (m³)
  const cubicMeters = liters / 1000;

  if (measurementUnit === 'ft') {
    if (unitIsUsg) {
      // Convert m³ to USG (US gallons): 1 m³ = 264.172 gallons
      const usg = cubicMeters * 264.172;
      return { value: formatNumberWithSeparator(usg), unit: 'USG' };
    }
    // Convert m³ to kgal (thousand gallons): 1 m³ = 0.264172 kgal
    const kgal = cubicMeters * 0.264172;
    return { value: formatNumberWithSeparator(kgal), unit: 'kgal' };
  }

  // Metric: show in m³ (rounded, no decimals)
  return { value: formatNumberWithSeparator(cubicMeters), unit: 'm³' };
};

const WaterSavedCard = ({ waterSavedActual, waterSavedPotential, t, units = 'm', unitIsUsg = false }) => {
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  const actualFormatted = formatWaterSaved(waterSavedActual, units, unitIsUsg);
  const potentialFormatted = formatWaterSaved(waterSavedPotential, units, unitIsUsg);
  const hasData = waterSavedActual != null || waterSavedPotential != null;

  const headerActions = (
    <Tooltip title={t('click_for_methodology_info') || 'Click for calculation methodology'} arrow PopperProps={{ sx: { zIndex: 20000 } }}>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); setInfoDialogOpen(true); }}
        sx={{
          backgroundColor: 'white',
          border: '1px solid #e8e8e8',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          padding: '4px',
          width: '24px',
          height: '24px',
          '&:hover': { backgroundColor: '#f5f5f5' }
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: DARK_GREY }}>?</Typography>
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      <BaseCard title={`${t('water_saved')} (${t('based_on_awwa_guidelines')})`} icon={<WaterDropIcon />} className="compact-card" headerActions={headerActions}>
        <Box display="flex" flexDirection="column" height="100%">
          <Box display="flex" alignItems="center" justifyContent="center" flex={1} gap={1.5}>
            <Box sx={statBoxStyle}>
              <Typography className="stat-value" sx={{ ...statValueStyle, color: ZIMA_BLUE }}>
                {hasData ? actualFormatted.value : 'N/A'}
              </Typography>
              <Typography sx={statLabelStyle}>{actualFormatted.unit} {t('calculated')}</Typography>
            </Box>
            <Box sx={statBoxStyle}>
              <Typography className="stat-value" sx={{ ...statValueStyle, color: ZIMA_BLUE }}>
                {hasData ? potentialFormatted.value : 'N/A'}
              </Typography>
              <Typography sx={statLabelStyle}>{potentialFormatted.unit} {t('potential')}</Typography>
            </Box>
          </Box>
        </Box>
      </BaseCard>

      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="md" fullWidth sx={{ zIndex: 20000 }}>
        <DialogTitle sx={{ fontWeight: 600, fontFamily: 'inherit', fontSize: '1.75rem' }}>
          {t('water_saved_methodology_title')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-line', fontFamily: 'inherit', fontSize: '1.25rem', lineHeight: 1.8 }}>
            {t('water_saved_methodology_content')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)} color="primary">{t('close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

WaterSavedCard.propTypes = {
  waterSavedActual: PropTypes.number,
  waterSavedPotential: PropTypes.number,
  t: PropTypes.func.isRequired,
  units: PropTypes.string,
  unitIsUsg: PropTypes.bool
};

export default WaterSavedCard;
